import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { TableHeader } from '@plato/ui-legacy/components/tables/table-header';
import { CopyButton } from '@plato/ui-legacy/components/ui/buttons/copy-button';
import { TabsHeader } from '@plato/ui-legacy/components/ui/tabs-header';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { DataTable } from '@plato/ui/components/data-table';
import { Container } from '@plato/ui/containers/container';
import { DialogBody, DialogHeader } from '@plato/ui/containers/dialog';
import { Button } from '@plato/ui/primitives/button';
import { cn } from '@plato/ui/util/classes';
import { ChevronLeftIcon, ExternalLinkIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '../../../components/page-title.tsx';
import { ExplorerTopLevelEntity } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { ExplorerView } from '../../explorer/components/explorer-view.tsx';
import { ExplorerViewProvider, type ExplorerState } from '../../explorer/hooks/use-explorer-query-params.ts';
import { useOrderColumnsForProductDetail } from '../../list-views/columns/use-order-columns-for-product-detail.tsx';
import { useQuoteColumnsForProductDetail } from '../../list-views/columns/use-quote-columns-for-product-detail.tsx';
import { InventoryDetailsAvailability } from './inventory-details-availability.tsx';
import { InventoryDetailsDescription } from './inventory-details-description.tsx';
import { InventoryDetailsInfo } from './inventory-details-info.tsx';
import { InventoryDetailsRecommendations } from './inventory-details-recommendations.tsx';
import { InventoryDetailsVariants } from './inventory-details-variants.tsx';

const PageTabs = {
    INFO: 'info',
    AVAILABILITY: 'availability',
    OPEN_QUOTES: 'quotes',
    OPEN_ORDERS: 'orders',
    EXPLORER: 'explorer',
} as const;

type PageTabs = (typeof PageTabs)[keyof typeof PageTabs];

type Props = {
    className?: string;
    erpProductId: string;
    onClose?: () => void;
    onBack?: () => void;
};

function InventoryDetails({ erpProductId, onBack, onClose, className }: Props) {
    const { t } = useTranslation();
    const { canReadOpenOrders, canReadQuotes, canReadProductVariants, webshopProductSearchUrl } =
        useAppConfig();
    const [selectedTab, setSelectedTab] = useState<PageTabs>(PageTabs.INFO);

    const getInventoryTabLabel = (tab: PageTabs): string => {
        switch (tab) {
            case PageTabs.AVAILABILITY:
                return t('tab_title__availability', { ns: 'product-details' });
            case PageTabs.EXPLORER:
                return t('tab_title__explorer', { ns: 'product-details' });
            case PageTabs.INFO:
                return t('tab_title__info', { ns: 'product-details' });
            case PageTabs.OPEN_ORDERS:
                return t('tab_title__orders', { ns: 'product-details' });
            case PageTabs.OPEN_QUOTES:
                return t('tab_title__quotes', { ns: 'product-details' });
        }
    };
    const [state, setState] = useState<Partial<ExplorerState>>({
        tab: ExplorerTopLevelEntity.CUSTOMER,
    });

    const sendEvent = useAnalytics();
    useEffect(() => {
        sendEvent('item_detail_page_viewed', {
            item_id: erpProductId,
        });
    }, [erpProductId, sendEvent]);

    const ordersColumns = useOrderColumnsForProductDetail();
    const quotesColumns = useQuoteColumnsForProductDetail();

    /* ============================================
     *  API Calls
     * ============================================
     */
    const {
        data: productDetails,
        isLoading: isLoadingDetail,
        isFetched: isFetchedDetail,
    } = api.platoUser.getDetailProduct.getProductDetail.useQuery({
        erpProductId,
    });

    const {
        data: ordersRawData,
        isLoading: isLoadingOrders,
        isFetchingNextPage: isFetchingOrdersNextPage,
        fetchNextPage: fetchNextPageOrders,
        isFetched: isFetchedOrders,
        hasNextPage: hasNextPageOrders,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.ORDER],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_ORDERS,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: ordersCount, isLoading: isLoadingOrdersCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.ORDER],
            },
            {
                enabled: isFetchedOrders,
            },
        );

    const {
        data: quotesRawData,
        isLoading: isLoadingQuotes,
        isFetchingNextPage: isFetchingQuotesNextPage,
        fetchNextPage: fetchNextPageQuotes,
        hasNextPage: hasNextPageQuotes,
        isFetched: isFetchedQuotes,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.QUOTE],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_QUOTES,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: quotesCount, isLoading: isLoadingQuotesCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.QUOTE],
            },
            {
                enabled: isFetchedQuotes,
            },
        );

    /* ============================================
     *  Clean Data
     * ============================================
     */

    const quotesData = useMemo(() => {
        if (quotesRawData) {
            return quotesRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [quotesRawData]);

    const ordersData = useMemo(() => {
        if (ordersRawData) {
            return ordersRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [ordersRawData]);

    /* ============================================
     *  Tables
     * ============================================
     */

    /* ============================================
     * Infinite scrolls
     * ============================================
     */

    if (isLoadingDetail || !productDetails) {
        return <LoadingBlockFullPage className="min-h-96" />;
    }

    const canReadProductAvailability = typeof productDetails.total_availability === 'number';

    const tabs = [
        PageTabs.INFO,
        ...(canReadProductAvailability ? [PageTabs.AVAILABILITY] : []),
        ...(canReadOpenOrders ? [PageTabs.OPEN_ORDERS] : []),
        ...(canReadQuotes ? [PageTabs.OPEN_QUOTES] : []),
        PageTabs.EXPLORER,
    ];

    return (
        <Container
            level={1}
            className={cn('flex h-full min-h-0 w-full grow flex-col bg-neutral-50', className)}
        >
            <PageTitle title={productDetails.product_variant_name} />
            {/* The tabs' own rule is the band's bottom edge, so the header suppresses its own. */}
            <DialogHeader className="flex-col items-stretch border-b-0 bg-white pb-0">
                {/* Long article names ran to a dozen lines on a phone before the clamp. */}
                <div className="flex items-start gap-default">
                    {onBack && (
                        <IconButton label={t('back', { ns: 'common' })} onClick={onBack}>
                            <ChevronLeftIcon />
                        </IconButton>
                    )}
                    <div className="flex min-w-0 grow flex-col">
                        <h1 className="line-clamp-2 text-heading-3">{productDetails.product_variant_name}</h1>
                        <div className="flex items-center gap-cozy text-caption text-neutral-500">
                            {t('product_information__id__product_id', {
                                ns: 'product-details',
                                value: productDetails.product_variant_id,
                            })}
                            <CopyButton textToCopy={productDetails.product_variant_id} />
                        </div>
                    </div>
                    {/* ponytail: search link, not a deep link; shop product URLs carry slugs Plato does not know */}
                    {webshopProductSearchUrl && (
                        <Button
                            href={`${webshopProductSearchUrl}${encodeURIComponent(productDetails.product_variant_id)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                sendEvent('webshop_opened', {
                                    source: 'item_detail_page',
                                    display_mode: window.matchMedia('(display-mode: standalone)').matches
                                        ? 'standalone'
                                        : 'browser',
                                });
                            }}
                        >
                            <ExternalLinkIcon />
                            {/* Icon only on phones; sr-only keeps the accessible name. */}
                            <span className="max-sm:sr-only">
                                {t('product_information__open_in_webshop', { ns: 'product-details' })}
                            </span>
                        </Button>
                    )}
                    {onClose && (
                        <IconButton label={t('form__close', { ns: 'common' })} onClick={onClose}>
                            <XIcon />
                        </IconButton>
                    )}
                </div>
                <TabsHeader<PageTabs>
                    selectedTabKey={selectedTab}
                    onTabChange={(value) => {
                        setSelectedTab(value);
                    }}
                    tabs={tabs}
                    getTabLabel={getInventoryTabLabel}
                />
            </DialogHeader>
            <DialogBody className="flex flex-col gap-container-cozy py-container-cozy">
                {selectedTab === PageTabs.INFO && (
                    <>
                        <InventoryDetailsInfo data={productDetails} />
                        <InventoryDetailsDescription data={productDetails} />
                        <InventoryDetailsRecommendations data={productDetails} />
                        {canReadProductVariants && (
                            <InventoryDetailsVariants
                                erpProductId={erpProductId}
                                fetchEnabled={isFetchedDetail}
                            />
                        )}
                    </>
                )}
                {selectedTab === PageTabs.AVAILABILITY && (
                    <InventoryDetailsAvailability erpProductId={erpProductId} />
                )}
                {selectedTab === PageTabs.OPEN_ORDERS && (
                    <>
                        <DataTable
                            emptyLabel={t('empty_table__no_results')}
                            isLoading={isLoadingOrders}
                            isFetchingNextPage={isFetchingOrdersNextPage}
                            onFetchNextPage={fetchNextPageOrders}
                            hasNextPage={hasNextPageOrders}
                            totalRowsCount={ordersCount}
                            data={ordersData}
                            columns={ordersColumns}
                        />
                        <TableHeader count={ordersCount} isLoadingCount={isLoadingOrdersCount} />
                    </>
                )}
                {selectedTab === PageTabs.OPEN_QUOTES && (
                    <>
                        <DataTable
                            emptyLabel={t('empty_table__no_results')}
                            isLoading={isLoadingQuotes}
                            isFetchingNextPage={isFetchingQuotesNextPage}
                            onFetchNextPage={fetchNextPageQuotes}
                            hasNextPage={hasNextPageQuotes}
                            totalRowsCount={quotesCount}
                            data={quotesData}
                            columns={quotesColumns}
                        />
                        <TableHeader count={quotesCount} isLoadingCount={isLoadingQuotesCount} />
                    </>
                )}
                {selectedTab === PageTabs.EXPLORER && (
                    <ExplorerViewProvider
                        value={{
                            state,
                            setState,
                            defaultFilters: {
                                product: [erpProductId],
                            },
                        }}
                    >
                        <ExplorerView pageSource="product" />
                    </ExplorerViewProvider>
                )}
            </DialogBody>
        </Container>
    );
}

export default InventoryDetails;
